import React, { Component, Fragment } from 'react'
import { H6, TinyText, fonts } from 'app/common/typography'
import { colors as oldColors } from 'shared/assets/styles'
import { colors } from 'app-v2/design-systems/theme/colors'
import styled from 'styled-components'
import { breakpoints, Button, media, style } from 'app/assets/styles'
import { Close, Star, Stroke } from 'app/assets/icons'
import type { IntlShape } from 'react-intl'
import { FormattedMessage, injectIntl } from 'react-intl'
import type { TransformForm } from 'shared/store/kingdom/points'
import { catalogType, promoType } from 'shared/utils'
import type { EventMetadata } from 'shared/store/metadata'
import { eventsDefinitions } from 'shared/store/metadata'
import type {
    CacPromotion,
    Coupon,
    SelectionScreenInfos,
    KingdomPoints
} from 'shared/types'
import type { ProcessCoupon } from 'shared/store/ecommerce/order'
import type { State as PrivilegesPageState } from 'shared/store/kingdom/privileges'
import type { MapById } from 'shared/store/ecommerce/catalog'
import { Picture } from 'app/common/picture'
import Modal from 'react-modal'
import { ProductSheet } from 'app/products'
import { PointsTutorialModal } from 'app/kingdom/tutorials'
import { Icon } from 'app-v2/shared/features/icon'
import { tokens } from 'app-v2/shared/features/icon/utils/tokens'
import { H9 } from 'app-v2/design-systems/typography/titles'
import { BORDER, SPACING } from 'app-v2/design-tokens'
import { string } from 'yup'
import { mapPickUpTypeToOrderType } from 'shared/store/ecommerce/catalog'

const { text } = oldColors
const { MENU, PRODUCT } = catalogType
const { medium } = breakpoints
const { classicModalStyle, classicModalMobileStyle, centeredModalStyle } = style

type Props = {
    title: string,
    images: Array<string>,
    routeId: string,
    type: string,
    legalNotice: string,
    promotionId: string,
    transformationId: string,
    levelPoints: number,
    points: number,
    pointsBeforeDiscount: number,
    collectMode?: string,
    orderTypeAvailabilities?: string[],
    firstLevel?: boolean,
    backgroundColor?: string,
    transformPoints: (
        form: TransformForm,
        fromEcommerce?: boolean
    ) => { pointsInfos: KingdomPoints, transformedCoupon: Coupon },
    untransformPoints: (couponId: string) => void,
    resetTransformPoints: () => void,
    transformPointsError?: string,
    sendMeta: EventMetadata => void,
    fromEcommerce?: boolean,
    processCouponNavigation: (
        coupon: Coupon,
        promotion: CacPromotion
    ) => ProcessCoupon,
    fetchPrivilegesData: () => PrivilegesPageState,
    promotionsById: MapById<CacPromotion>,
    navigation: (to: string, state?: any, refresh?: boolean) => void,
    intl: IntlShape,
    openSelectionScreen?: (
        selectionScreenInfos: SelectionScreenInfos,
        error?: string
    ) => void,
    loadingPointsTransformation?: (loading: boolean) => void,
    locked?: boolean
}

type State = {
    displayProductSheet: boolean,
    openModal: boolean,
    shouldShowTuto: boolean,
    width: number
}

class Offer extends Component<Props, State> {
    constructor() {
        super()
        this.state = {
            displayProductSheet: false,
            openModal: false,
            shouldShowTuto: false,
            width: window.innerWidth
        }
    }

    componentDidMount() {
        window.addEventListener('resize', this.updateWindowDimensions)
    }

    componentWillUnmount() {
        window.removeEventListener('resize', this.updateWindowDimensions)
    }

    updateWindowDimensions = () => this.setState({ width: window.innerWidth })

    handleTransformPoints = async () => {
        const {
            title,
            promotionId,
            transformationId,
            transformPoints,
            collectMode,
            untransformPoints,
            resetTransformPoints,
            sendMeta,
            fetchPrivilegesData,
            processCouponNavigation,
            promotionsById,
            navigation,
            intl,
            openSelectionScreen,
            loadingPointsTransformation
        } = this.props
        try {
            const meta = {
                ...eventsDefinitions.KINGDOM_CROWN_TRANSFORM,
                event_label: title
            }
            sendMeta(meta)
            resetTransformPoints()
            loadingPointsTransformation && loadingPointsTransformation(true)
            const { transformedCoupon } = await transformPoints({
                transformationId,
                pickUpType: collectMode
            })
            const promotion = promotionsById[promotionId || '']
            if (promotion && transformedCoupon) {
                const CCpromo = {
                    couponCode: transformedCoupon.id,
                    idReboot: promotion.idReboot,
                    promoType: promotion.promoType,
                    promoValue: promotion.promoValue || 0,
                    promotionId,
                    promotionName: promotion.name,
                    threshold: promotion.threshold
                }
                const {
                    listItems,
                    type,
                    error,
                    hasOptions
                    //$FlowFixMe
                } = await processCouponNavigation(transformedCoupon, promotion)
                if (listItems || error) {
                    openSelectionScreen &&
                        openSelectionScreen(
                            { listItems, type, promotion: CCpromo },
                            error
                        )
                } else {
                    let route
                    switch (type) {
                        case MENU:
                            route = 'routes.clickandcollect.menu'
                            break
                        case PRODUCT:
                            route = hasOptions
                                ? 'routes.clickandcollect.product'
                                : 'routes.clickandcollect.catalog'
                            break
                        case promoType.ORDER:
                            route = 'routes.clickandcollect.cart.summary'
                            break
                        default:
                            break
                    }
                    if (route) {
                        navigation(
                            intl.formatMessage({
                                id: route
                            }),
                            {
                                promotion: CCpromo
                            }
                        )
                    }
                }
            } else {
                transformedCoupon && untransformPoints(transformedCoupon.id)
                navigation(
                    intl.formatMessage({
                        id: 'routes.clickandcollect.promotions'
                    }),
                    null,
                    true
                )
            }
        } catch (error) {
            console.error('Error in point transformation :', error)
        } finally {
            fetchPrivilegesData()
            loadingPointsTransformation && loadingPointsTransformation(false)
        }
    }

    onEyeClick = () => {
        this.setState({
            displayProductSheet: true
        })
    }

    renderProductSheetModal = () => {
        const { routeId, type, intl } = this.props
        const { displayProductSheet, width } = this.state
        const productRoute = intl.formatMessage({ id: 'routes.product' })
        const menuRoute = intl.formatMessage({ id: 'routes.menu' })
        const isMenu = type === MENU
        return (
            <Modal
                isOpen={displayProductSheet}
                style={
                    width < medium
                        ? classicModalMobileStyle
                        : centeredModalStyle
                }
                centered>
                <CloseButtonContainer
                    onClick={() =>
                        this.setState({
                            displayProductSheet: false
                        })
                    }>
                    <Close color={text} size={24} />
                </CloseButtonContainer>
                <ProductSheet
                    productPath={`${
                        isMenu ? menuRoute : productRoute
                    }/${routeId}`}
                    fromEcommerce={true}
                />
            </Modal>
        )
    }

    showTutorial = () => {
        const { shouldShowTuto } = this.state
        this.setState({ shouldShowTuto: !shouldShowTuto })
    }

    render() {
        const {
            title,
            images,
            routeId,
            levelPoints,
            locked,
            firstLevel,
            backgroundColor,
            fromEcommerce,
            points,
            pointsBeforeDiscount,
            collectMode,
            orderTypeAvailabilities
        } = this.props
        const { shouldShowTuto, width } = this.state

        const isPickupTypeAvailable = () => {
            if (!fromEcommerce || !collectMode) return false
            if (!orderTypeAvailabilities) return true
            if (orderTypeAvailabilities) {
                return orderTypeAvailabilities.includes(
                    mapPickUpTypeToOrderType(collectMode)
                )
            }
            return false
        }

        const isInPromotion = !!pointsBeforeDiscount && isPickupTypeAvailable()

        return (
            <Fragment>
                <Modal
                    isOpen={shouldShowTuto}
                    ariaHideApp={false}
                    style={
                        width < medium
                            ? classicModalMobileStyle
                            : classicModalStyle
                    }>
                    <PointsTutorialModal handleClose={this.showTutorial} />
                </Modal>
                <TileContainer
                    locked={locked}
                    backgroundColor={
                        isInPromotion ? colors.white : backgroundColor
                    }
                    borderColor={
                        isInPromotion ? colors.fieryRed : backgroundColor
                    }
                    onClick={() => {
                        if (!!locked) return
                        if (fromEcommerce) {
                            this.handleTransformPoints()
                        } else {
                            this.showTutorial()
                        }
                    }}>
                    {isInPromotion && (
                        <StyledPromoTextContainer
                            backgroundColor={backgroundColor}>
                            <Star />
                            <StyledTinyText
                                color={colors.mayoEggWhite}
                                font={fonts.flameSansRegular}>
                                <FormattedMessage id="kingdom.points.offer.promotion" />
                            </StyledTinyText>
                        </StyledPromoTextContainer>
                    )}
                    <Tile>
                        <TitleContainer>
                            <StyledH6
                                color={
                                    firstLevel || isInPromotion
                                        ? colors.bbqBrown
                                        : colors.mayoEggWhite
                                }>
                                {title}
                            </StyledH6>
                        </TitleContainer>
                        <ProductImage>
                            <Picture singleImage imageUrl={images} />
                        </ProductImage>
                        <Info>
                            {!!routeId && (
                                <Button
                                    onClick={e => {
                                        e.stopPropagation()
                                        this.onEyeClick()
                                    }}>
                                    <Icon
                                        code={tokens.eye}
                                        size={20}
                                        color={
                                            firstLevel || isInPromotion
                                                ? colors.bbqBrown
                                                : colors.mayoEggWhite
                                        }
                                    />
                                </Button>
                            )}
                            <Price>
                                <H9
                                    color={
                                        firstLevel || isInPromotion
                                            ? colors.bbqBrown
                                            : colors.mayoEggWhite
                                    }>
                                    {isInPromotion
                                        ? points
                                        : pointsBeforeDiscount ?? levelPoints}
                                </H9>
                                {isInPromotion && (
                                    <StyledPointBeforeDiscountPrice>
                                        <PointsBeforeDiscountH9
                                            color={
                                                firstLevel || isInPromotion
                                                    ? colors.bbqBrown
                                                    : colors.mayoEggWhite
                                            }>
                                            {pointsBeforeDiscount}
                                        </PointsBeforeDiscountH9>
                                        <StyledStroke>
                                            <Stroke
                                                color={
                                                    isInPromotion &&
                                                    colors.bbqBrown
                                                }
                                            />
                                        </StyledStroke>
                                    </StyledPointBeforeDiscountPrice>
                                )}
                                <Icon
                                    code={tokens.crown}
                                    size={20}
                                    color={
                                        firstLevel || isInPromotion
                                            ? colors.meltyYellow
                                            : colors.mayoEggWhite
                                    }
                                    marginLeft={5}
                                    marginTop={2}
                                />
                            </Price>
                        </Info>
                    </Tile>
                </TileContainer>
                {this.renderProductSheetModal()}
            </Fragment>
        )
    }
}

const ProductImage = styled.div`
    width: 75%;
    margin: auto;
    height: 75px;
`

const TileContainer = styled.div`
    padding: 15px;
    align-items: center;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    border-radius: ${BORDER.RADIUS.M};
    box-sizing: border-box;
    ${props => props.locked && 'opacity: 0.5'};
    align-content: center;
    margin: ${SPACING.S} ${SPACING.XXXS} ${SPACING.XXL};
    background-color: ${props => props.backgroundColor};
    border: ${BORDER.WIDTH.M} solid ${props => props.borderColor};
    position: relative;
    margin-top: 6px;
`

const Tile = styled.div`
    width: 110px;
    height: 110px;
`

const TitleContainer = styled.div`
    height: 20px;
`

const Info = styled.div`
    display: flex;
    flex-direction: row;
    justify-content: space-between;
`

const Price = styled.div`
    display: flex;
    justify-content: flex-start;
    align-items: center;
`

const StyledH6 = styled(H6)`
    align-self: flex-start;
    margin: -${SPACING.XXXS} 0 0 -${SPACING.XXXS};
    line-height: 15px;
    ${media.medium`
        font-size: 16px;
    `};
`

const CloseButtonContainer = styled.div`
    margin: ${SPACING.M};
    cursor: pointer;
`

const PointsBeforeDiscountH9 = styled(H9)`
    opacity: 0.5;
`

const StyledPointBeforeDiscountPrice = styled.div`
    position: relative;
    margin-left: ${SPACING.XXXS};
    box-sizing: border-box;
`

const StyledStroke = styled.div`
    position: absolute;
    top: 60%;
    left: 50%;
    transform: translate(-50%, -50%);
`

const StyledPromoTextContainer = styled.div`
    display: flex;
    flex-direction: row;
    gap: ${SPACING.XXXS};
    align-items: center;
    border-radius: 50px;
    background-color: ${colors.fieryRed};
    position: absolute;
    padding: 2px ${SPACING.XXS};
    top: -10px;
    box-sizing: border-box;
`

const StyledTinyText = styled(TinyText)`
    line-height: 0;
`

const OfferComponent = injectIntl(Offer)

export { OfferComponent }
